import * as queries from 'lib/graphql/queries';
import storefront from 'lib/storefront';
import {
  encodeObjectID,
  convertGQLToProductJson,
  handleize,
} from 'utils/general';

/**
 * @info
 *
 * *The "store/shade-finder" directory*
 * This "store" directory declares each of the Vuex
 * modules that all together power the site. Here, we have the
 * Vuex store module that sits behind product functionality.
 *
 * *This file*
 * This file outlines "actions". Actions are functions that
 * include logic that, when ready, commits a mutation to the
 * application state. Whereas mutations must be synchronous,
 * actions return a promise and can include asynchronous
 * logical sequences.
 */

const getTagsArray = (formattedProducts, state) => {
  return [
    ...new Set(
      formattedProducts
        .map((el) => {
          return el.tags;
        })
        .flat()
        .filter((item) => {
          const formattedItem = item?.split(':')[0];
         return state.filterGroups.includes(formattedItem);
        })
    ),
  ];
};

const getMicroCollectionTitle = (product) => {
  return product.title?.split(__GLOBAL__.productDelimiter)[0].trim();
};

const constructMatchedProducts = (formattedProducts, filterData, state) => {
  const matchedProducts = {
    cooler: {},
    lighter: {},
    recommended: {},
    deeper: {},
    warmer: {},
  };

  for (const [key, value] of Object.entries(state.recommendationIds)) {
    matchedProducts[key] = [...value].reduce((acc, id) => {
      const matchedProduct = formattedProducts.filter((product) => {
        return product.id === id;
      });

      if (matchedProduct.length > 0) {
        // Build filters object and attach to product
        // Makes filtering more efficient
        for (const [key, _] of Object.entries(filterData)) {
          const found = matchedProduct[0].tags.find((tag) => {
            return tag.includes(`${key}:`);
          });

          if (found) {
            const filterValue = found.split(':')[1];
            matchedProduct[0].filters = {
              ...matchedProduct[0].filters,
              [key]: filterValue,
            };
          }
        }

        const microCollectionTitle = getMicroCollectionTitle(matchedProduct[0]);
        if (Object.hasOwn(acc, microCollectionTitle)) {
          acc[microCollectionTitle] = [
            ...acc[microCollectionTitle],
            matchedProduct[0],
          ];
        } else {
          acc[microCollectionTitle] = [matchedProduct[0]];
        }
      }
      return acc;
    }, {});
  }
  return matchedProducts;
};

const createFilterData = (uniqueTags) => {
  const tagsArr = uniqueTags.map((el) => {
    const item = el.replace(/['"]+/g, '');
    return { [item.split(':')[0]]: item.split(':')[1] };
  }, []);

  const dataObj = tagsArr.reduce((acc, rec) => {
    Object.entries(rec).forEach(([key, v]) =>
      key in acc ? acc[key].push(v) : (acc[key] = [v]),
    );
    return acc;
  }, {});

  const filterDataObj = Object.entries(dataObj).reduce((acc, [key, value]) => {
    return { ...acc, [key]: value };
  }, []);

  return filterDataObj;
};

export default {
  resetVto({ commit }) {
    commit('setVtoStep', 2);
    commit('setRecommendations', []);
    commit('setFiltersData', {});
    commit('setActiveGroup', 'recommended');
    YMK.backToShadeFinder();
  },
  setRecommendations({ commit }, payload) {
    commit('setRecommendations', payload);
  },
  setFiltersData({ commit }, payload) {
    commit('setFiltersData', payload);
  },
  setVtoStep({ commit }, payload) {
    commit('setVtoStep', payload);
  },
  setActiveGroup({ commit }, payload) {
    commit('setActiveGroup', payload);
  },
  addActiveFilter({ commit }, payload) {
    commit('setActiveFilters', payload);
  },
  resetFilterByKey({ commit }, payload) {
    commit('resetFilterByKey', payload);
  },
  clearFilters({ commit }) {
    commit('setActiveFilters', {});
  },
  async processRecommendations({ commit, state }, payload) {
    const processed = payload.reduce(
      (acc, curr) => {
        const { cooler, darker, lighter, matched, warmer } = curr;
        if (cooler.guid) {
          acc['cooler'].add(cooler.skuId);
        }

        if (darker.guid) {
          acc['deeper'].add(darker.skuId);
        }

        if (lighter.guid) {
          acc['lighter'].add(lighter.skuId);
        }

        if (matched.guid) {
          acc['recommended'].add(matched.skuId);
        }

        if (warmer.guid) {
          acc['warmer'].add(warmer.skuId);
        }

        return acc;
      },
      {
        cooler: new Set(),
        lighter: new Set(),
        recommended: new Set(),
        deeper: new Set(),
        warmer: new Set(),
      },
    );

    commit('setRecommendationIds', processed);

    const allProductIds = new Set([
      ...processed['cooler'],
      ...processed['lighter'],
      ...processed['recommended'],
      ...processed['deeper'],
      ...processed['warmer'],
    ]);

    const products = await storefront.request(queries.products, {
      ids: [...allProductIds].map((id) => encodeObjectID(id)),
    });

    if (Boolean(products)) {
      const formattedProducts = products.nodes.map((product) =>
        convertGQLToProductJson(product),
      );
      const uniqueTags = getTagsArray(formattedProducts, state);
      const filterData = createFilterData(uniqueTags);

      const matchedProducts = constructMatchedProducts(
        formattedProducts,
        filterData,
        state,
      );
      commit('setRecommendations', matchedProducts);
      commit('setFiltersData', filterData);

      const activeMicroCollectionTitle = Object.keys(
        state.recommendations[state.activeGroup],
      )[0];
      if (Boolean(activeMicroCollectionTitle)) {
        commit('setActiveMicroCollectionTitle', activeMicroCollectionTitle);
      }
    }
  },
  async setCurrentMicroCollectionDescription({ commit, state }) {
    const collectionHandle = handleize(state.activeMicroCollectionTitle);
    if (!Boolean(collectionHandle)) {
      commit('setActiveMicroCollectionDescription', '');
      return;
    }

    const response = await storefront.request(
      queries.microCollectionMetafields,
      { handle: collectionHandle },
    );
    if (Boolean(response.collection.metafields.length)) {
      const description = response.collection.metafields.filter((field) => {
        if (Boolean(field)) {
          return field.key === 'short_description';
        }
      });

      if (Boolean(description.length)) {
        commit('setActiveMicroCollectionDescription', description[0].value);
      } else {
        commit('setActiveMicroCollectionDescription', '');
      }
    }
  },
};
